import React, { useState, useEffect } from 'react';
import { Search, Filter, Calendar, Grid3x3 as Grid3X3, List, Import as SortAsc, Clock, Users, ExternalLink, RefreshCw, Building2, Phone, Mail, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import StatusBadge from '../components/ui/StatusBadge';
import { useAuth } from '../components/AuthProvider';
import { getAppointments, getAppointmentsCount, getAppointmentStats, createAppointment, getClients } from '../lib/api';
import { getAccessibleClientIds } from '../lib/multiClientUtils';
import { formatDateTime } from '../lib/utils';
import { usePagination } from '../hooks/usePagination';
import { FILTER_OPTIONS, matchesStatusFilter } from '../lib/appointmentStatus';
import Pagination from '../components/ui/Pagination';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal from '../components/ui/Modal';
import AddAppointmentForm from '../components/AddAppointmentForm';
import Button from '../components/ui/Button';
import EdenredStatusBadge from '../components/EdenredStatusBadge';

const AppointmentsPage: React.FC = () => {
  const { profile, isAuthenticated } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'date' | 'status' | 'recent'>('recent');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [allAppointments, setAllAppointments] = useState([]);
  const [isAddAppointmentModalOpen, setIsAddAppointmentModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [availableClients, setAvailableClients] = useState<Array<{id: string, name: string}>>([]);
  const [stats, setStats] = useState({
    total: 0,
    effectue: 0,
    planifie: 0,
    confirme: 0,
    annule: 0,
    reporte: 0,
  });

  // Pagination hook
  const pagination = usePagination({
    initialPageSize: 20,
    enableLogging: true,
  });

  const fetchAppointments = async (page: number = 1, pageSize: number = 20) => {
    if (!profile || !isAuthenticated) {
      console.log('Not authenticated, skipping fetch');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let clientIds = await getAccessibleClientIds(profile.user_id, profile.role);

      if (clientFilter !== 'all') {
        clientIds = clientIds.filter(id => id === clientFilter);
      }

      if (clientIds.length === 0 && profile.role !== 'super_admin' && profile.role !== 'admin') {
        console.log('No accessible clients found');
        setAppointments([]);
        pagination.setTotalItems(0);
        setStats({ total: 0, effectue: 0, planifie: 0, confirme: 0, annule: 0, reporte: 0 });
        setIsLoading(false);
        return;
      }

      const offset = (page - 1) * pageSize;

      const [appointmentsData, totalCount, statsData] = await Promise.all([
        getAppointments(clientIds, { limit: pageSize, offset, orderBy: 'created_at', orderDirection: 'desc' }),
        getAppointmentsCount(clientIds),
        getAppointmentStats()
      ]);

      setAppointments(appointmentsData);
      pagination.setTotalItems(totalCount);
      setStats(statsData);

      if (page === 1 && !searchTerm) {
        try {
          const allData = await getAppointments(clientIds, { limit: 1000, orderBy: 'created_at', orderDirection: 'desc' });
          setAllAppointments(allData);
        } catch (err) {
          console.warn('Could not load all appointments for search:', err);
          setAllAppointments(appointmentsData);
        }
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setError('Erreur lors du chargement des rendez-vous');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAvailableClients = async () => {
      if (!profile || !isAuthenticated) return;

      try {
        const clients = await getClients();
        setAvailableClients(clients.map(c => ({ id: c.id, name: c.name })));
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchAvailableClients();
  }, [profile, isAuthenticated]);

  useEffect(() => {
    if (profile && isAuthenticated) {
      fetchAppointments(1, pagination.pageSize);
    }
  }, [profile, isAuthenticated, clientFilter]);

  useEffect(() => {
    if (profile && isAuthenticated && pagination.currentPage > 1) {
      fetchAppointments(pagination.currentPage, pagination.pageSize);
    }
  }, [pagination.currentPage, pagination.pageSize, profile, isAuthenticated]);

  const handleRefresh = async () => {
    if (!profile || !isAuthenticated || isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    setError(null);

    try {
      // Reset to first page and fetch fresh data
      pagination.setPage(1);
      await fetchAppointments(1, pagination.pageSize);
    } catch (error) {
      console.error('Error refreshing appointments:', error);
      setError('Erreur lors de l\'actualisation des rendez-vous');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter and sort appointments - this runs on the current page data
  useEffect(() => {
    // Utiliser tous les rendez-vous pour la recherche, sinon les rendez-vous de la page actuelle
    let sourceData = searchTerm.trim() ? allAppointments : appointments;
    let filtered = [...sourceData];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(appointment =>
        (appointment.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (appointment.prospect_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (appointment.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (appointment.clients?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(appointment => matchesStatusFilter(appointment.status, statusFilter));
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      const weekStartStr = weekStart.toISOString().split('T')[0];
      
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthStartStr = monthStart.toISOString().split('T')[0];
      
      filtered = filtered.filter(appointment => {
        const appointmentDate = appointment.date;
        if (!appointmentDate) return false;
        
        switch (dateFilter) {
          case 'today':
            return appointmentDate === todayStr;
          case 'yesterday':
            return appointmentDate === yesterdayStr;
          case 'this_week':
            return appointmentDate >= weekStartStr && appointmentDate <= todayStr;
          case 'this_month':
            return appointmentDate >= monthStartStr;
          case 'past':
            return appointmentDate < todayStr;
          case 'future':
            return appointmentDate > todayStr;
          default:
            return true;
        }
      });
    }
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'date':
          return new Date(`${b.date}T${b.time}`).getTime() - new Date(`${a.date}T${a.time}`).getTime();
        case 'status':
          return a.status.localeCompare(b.status);
        case 'recent':
          return new Date(b.booking_date || b.created_at).getTime() - new Date(a.booking_date || a.created_at).getTime();
        default:
          return 0;
      }
    });

    setFilteredAppointments(filtered);
  }, [searchTerm, appointments, allAppointments, sortBy, statusFilter]);

  const getStatusBadge = (status: string) => {
    return <StatusBadge status={status} size="sm" />;
  };

  const statusOptions = FILTER_OPTIONS;

  const dateOptions = [
    { value: 'all', label: 'Toutes les dates' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'yesterday', label: 'Hier' },
    { value: 'this_week', label: 'Cette semaine' },
    { value: 'this_month', label: 'Ce mois' },
    { value: 'past', label: 'Passés' },
    { value: 'future', label: 'À venir' },
  ];
  const sortOptions = [
    { value: 'recent', label: 'Plus récents', icon: SortAsc },
    { value: 'date', label: 'Date RDV', icon: SortAsc },
    { value: 'title', label: 'Titre (A-Z)', icon: SortAsc },
    { value: 'status', label: 'Statut', icon: SortAsc },
  ];

  if (isLoading && appointments.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" text="Chargement des rendez-vous..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Title and Description */}
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 bg-clip-text text-transparent">
                Rendez-vous
              </h1>
              <p className="text-gray-600 mt-2 text-lg">
                Gérez tous vos rendez-vous clients et prospects
              </p>
              {successMessage && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-3 bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {successMessage}
                </motion.div>
              )}
              {error && (
                <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg">
                  {error}
                </div>
              )}
              {!isLoading && (
                <div className="flex items-center mt-3 text-sm text-gray-500">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>{pagination.totalItems} rendez-vous au total</span>
                </div>
              )}
            </div>

            {/* Search and Controls */}
            <div className="flex flex-col sm:flex-row gap-4 lg:min-w-96">
              {/* Add Appointment Button */}
              <Button
                onClick={() => setIsAddAppointmentModalOpen(true)}
                icon={<Plus className="h-5 w-5" />}
                className="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 whitespace-nowrap"
              >
                Nouveau RDV
              </Button>
              
              {/* Search Bar */}
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Rechercher un rendez-vous..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full px-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 rounded-xl border transition-all duration-200 flex items-center space-x-2 ${
                  showFilters 
                    ? 'bg-blue-500 text-white border-blue-500 shadow-lg' 
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm hover:shadow-md'
                }`}
              >
                <Filter className="h-5 w-5" />
                <span className="hidden sm:inline">Filtres</span>
              </button>

              {/* View Mode Toggle - Moved outside filters */}
              <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-all duration-200 ${
                    viewMode === 'grid'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-all duration-200 ${
                    viewMode === 'list'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm hover:shadow-md transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{isRefreshing ? 'Actualisation...' : 'Actualiser'}</span>
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden"
              >
                <div className="p-6">
                  <div className="space-y-6">
                    {/* Sort Options */}
                    <div>
                      <span className="text-sm font-medium text-gray-700 mb-3 block">Trier par :</span>
                      <div className="flex flex-wrap gap-2">
                        {sortOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setSortBy(option.value as any)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                              sortBy === option.value
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            <option.icon className="h-4 w-4" />
                            <span>{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Date Filter */}
                    <div>
                      <span className="text-sm font-medium text-gray-700 mb-3 block">Filtrer par date :</span>
                      <div className="flex flex-wrap gap-2">
                        {dateOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setDateFilter(option.value)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              dateFilter === option.value
                                ? 'bg-green-100 text-green-700 border border-green-200'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Status Filter */}
                    <div>
                      <span className="text-sm font-medium text-gray-700 mb-3 block">Filtrer par statut :</span>
                      <div className="flex flex-wrap gap-2">
                        {statusOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setStatusFilter(option.value)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              statusFilter === option.value
                                ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Client Filter - Only show for users with multiple clients */}
                    {availableClients.length > 1 && (
                      <div>
                        <span className="text-sm font-medium text-gray-700 mb-3 block">Filtrer par client :</span>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setClientFilter('all')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              clientFilter === 'all'
                                ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            Tous les clients
                          </button>
                          {availableClients.map((client) => (
                            <button
                              key={client.id}
                              onClick={() => setClientFilter(client.id)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                clientFilter === client.id
                                  ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                              }`}
                            >
                              {client.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Search Results Info */}
          {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all' || clientFilter !== 'all') && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Search className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    {filteredAppointments.length} résultat{filteredAppointments.length !== 1 ? 's' : ''} trouvé{filteredAppointments.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-blue-700">
                    {searchTerm && `pour "${searchTerm}"`}
                    {searchTerm && (statusFilter !== 'all' || dateFilter !== 'all') && ' • '}
                    {statusFilter !== 'all' && `statut: ${statusOptions.find(s => s.value === statusFilter)?.label}`}
                    {statusFilter !== 'all' && dateFilter !== 'all' && ' • '}
                    {dateFilter !== 'all' && `date: ${dateOptions.find(d => d.value === dateFilter)?.label}`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateFilter('all');
                  }}
                  className="ml-auto text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Effacer
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Stats Section - Moved to top */}
        {!isLoading && stats.total > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8 bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              <div>
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                  <div className="text-sm text-blue-700 font-medium">Total RDV</div>
                </div>
              </div>
              <div>
                <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                  <div className="text-2xl font-bold text-green-600">{stats.effectue}</div>
                  <div className="text-sm text-green-700 font-medium">Effectués</div>
                </div>
              </div>
              <div>
                <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-600">{stats.planifie}</div>
                  <div className="text-sm text-yellow-700 font-medium">Planifiés</div>
                </div>
              </div>
              <div>
                <div className="text-center p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                  <div className="text-2xl font-bold text-indigo-600">{stats.confirme}</div>
                  <div className="text-sm text-indigo-700 font-medium">Confirmés</div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Appointments Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {/* Loading overlay for pagination */}
          {isLoading && appointments.length > 0 && (
            <div className="mb-6 flex justify-center">
              <LoadingSpinner text="Chargement des rendez-vous..." />
            </div>
          )}

          {filteredAppointments.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                <Calendar className="h-12 w-12 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isLoading ? 'Chargement...' : 'Aucun rendez-vous trouvé'}
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Aucun rendez-vous ne correspond à vos critères de recherche.'
                  : isLoading ? 'Veuillez patienter...' : 'Aucun rendez-vous planifié pour le moment.'
                }
              </p>
            </motion.div>
          ) : (
            <div className={viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
              : "space-y-4"
            }>
              {filteredAppointments.map((appointment, index) => (
                <motion.div
                  key={appointment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={viewMode === 'grid' ? { y: -4 } : {}}
                  className="group"
                >
                  {viewMode === 'grid' ? (
                    <Link
                      to={`/appointments/${appointment.id}`}
                      className="block bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300"
                    >
                      {/* Header avec statut */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                          {appointment.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {appointment.clients?.name}
                          </p>
                        </div>
                        {getStatusBadge(appointment.status)}
                      </div>

                      {/* Informations principales */}
                      <div className="space-y-3 mb-4">
                        {/* Date et heure */}
                        <div className="flex items-center text-sm">
                          <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <Clock className="h-4 w-4 text-blue-500" />
                          </div>
                          <span className="font-medium text-gray-900">
                            {formatDateTime(appointment.date, appointment.time)}
                          </span>
                        </div>
                        
                        {/* Prospect */}
                        <div className="flex items-center text-sm">
                          <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <Users className="h-4 w-4 text-purple-500" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 truncate">{appointment.prospect_name}</div>
                            <div className="text-gray-500 truncate">
                              {appointment.job_title ? `${appointment.job_title} • ${appointment.company_name}` : appointment.company_name}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Footer avec indicateur d'action */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex flex-col gap-2">
                          <span className="text-xs text-gray-500 font-medium">Voir les détails</span>
                          <EdenredStatusBadge
                            clientId={appointment.client_id}
                            edenredStatus={appointment.appointments_edenred?.[0]?.edenred_status}
                          />
                        </div>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-blue-200 rounded-full group-hover:bg-blue-400 transition-colors"></div>
                          <div className="w-2 h-2 bg-blue-300 rounded-full group-hover:bg-blue-500 transition-colors delay-75"></div>
                          <div className="w-2 h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 transition-colors delay-150"></div>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <Link
                      to={`/appointments/${appointment.id}`}
                      className="block bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all duration-300"
                    >
                      <div className="flex items-start space-x-4">
                        {/* Avatar avec statut */}
                        <div className="relative flex-shrink-0">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                            <Calendar className="h-6 w-6 text-white" />
                          </div>
                          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                            appointment.status === 'Completed' || appointment.status === 'effectué' ? 'bg-green-400' :
                            appointment.status === 'Confirmed' || appointment.status === 'confirmé' ? 'bg-blue-400' :
                            appointment.status === 'Scheduled' || appointment.status === 'planifié' ? 'bg-yellow-400' :
                            appointment.status === 'Cancelled' || appointment.status === 'annulé' ? 'bg-red-400' :
                            appointment.status === 'Postponed' || appointment.status === 'reporté' ? 'bg-orange-400' :
                            'bg-gray-400'
                          }`}></div>
                        </div>

                        {/* Contenu principal */}
                        <div className="flex-1 min-w-0">
                          {/* Ligne 1: Titre et badge statut */}
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate pr-4">
                              {appointment.title}
                            </h3>
                            {getStatusBadge(appointment.status)}
                          </div>

                          {/* Ligne 2: Informations principales en colonnes */}
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
                            {/* Date et heure */}
                            <div className="flex items-center bg-blue-50 rounded-lg px-3 py-2">
                              <Clock className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-blue-700 truncate">
                                  {formatDateTime(appointment.date, appointment.time)}
                                </div>
                              </div>
                            </div>

                            {/* Contact prospect */}
                            <div className="flex items-center bg-purple-50 rounded-lg px-3 py-2">
                              <Users className="h-4 w-4 text-purple-500 mr-2 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-purple-700 truncate">
                                  {appointment.prospect_name}
                                </div>
                                {appointment.job_title && (
                                  <div className="text-xs text-purple-600 truncate">
                                    {appointment.job_title}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Entreprise */}
                            <div className="flex items-center bg-green-50 rounded-lg px-3 py-2">
                              <Building2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-green-700 truncate">
                                  {appointment.company_name}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Ligne 3: Informations secondaires */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              {/* Client */}
                              {appointment.clients && (
                                <div className="flex items-center">
                                  <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                                  <span>Client: {appointment.clients.name}</span>
                                </div>
                              )}

                              {/* Edenred Badge */}
                              <EdenredStatusBadge
                                clientId={appointment.client_id}
                                edenredStatus={appointment.appointments_edenred?.[0]?.edenred_status}
                              />

                              {/* Téléphone */}
                              {appointment.phone && (
                                <div className="hidden sm:flex items-center">
                                  <Phone className="h-3 w-3 mr-1 text-gray-400" />
                                  <span>{appointment.phone}</span>
                                </div>
                              )}
                            </div>

                            {/* Action */}
                            <div className="flex items-center text-blue-600 group-hover:text-blue-700">
                              <span className="text-sm font-medium mr-2">Voir détails</span>
                              <ExternalLink className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Pagination */}
        {!isLoading && pagination.totalPages > 1 && !searchTerm && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.setPage}
              isLoading={isLoading}
              pageSize={pagination.pageSize}
              onPageSizeChange={pagination.setPageSize}
              totalItems={pagination.totalItems}
            />
          </motion.div>
        )}

        {/* Add Appointment Modal */}
        <Modal
          isOpen={isAddAppointmentModalOpen}
          onClose={() => setIsAddAppointmentModalOpen(false)}
          title="Créer un nouveau rendez-vous"
          size="xl"
        >
          <AddAppointmentForm
            onSuccess={(newAppointment) => {
              setIsAddAppointmentModalOpen(false);
              setSuccessMessage(`Rendez-vous "${newAppointment.title}" créé avec succès`);
              
              // Clear success message after 5 seconds
              setTimeout(() => {
                setSuccessMessage(null);
              }, 5000);
              
              // Refresh the appointments list
              fetchAppointments(1, pagination.pageSize);
            }}
            onCancel={() => setIsAddAppointmentModalOpen(false)}
          />
        </Modal>
      </div>
    </div>
  );
};

export default AppointmentsPage;